import React, { useState, useCallback } from 'react';
import { useAppQuery } from '../hooks/useAppQuery';
import duxlyLogo from '../assets/duxly-logo.png';
import {
  Page,
  LegacyCard,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  ProgressBar,
  ButtonGroup,
  Button,
  Filters,
  Modal,
  Select,
} from '@shopify/polaris';

const ProductList = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalActive, setModalActive] = useState(false);
  const [bulkAction, setBulkAction] = useState('generate');

  const handleSelectionChange = useCallback(
    (selectedItems) => {
      setSelectedItems(selectedItems);
    },
    [],
  );

  const handleBulkAction = useCallback(() => {
    setModalActive(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalActive(false);
    setBulkAction('generate');
  }, []);

  const handleModalAction = useCallback(() => {
    // Here you would implement the actual bulk action
    console.log(`Performing ${bulkAction} on`, selectedItems);
    setModalActive(false);
    setSelectedItems([]);
  }, [selectedItems, bulkAction]);

  const bulkActions = [
    {
      content: 'Generate content',
      onAction: handleBulkAction,
    },
  ];

  const {
    data: productsData,
    isLoading,
    isError,
    error,
    refetch
  } = useAppQuery('/api/products');

  const products = productsData?.products || [];

  const actionOptions = [
    { label: 'Generate content', value: 'generate' },
    { label: 'Regenerate content', value: 'regenerate' },
    { label: 'Delete content', value: 'delete' },
  ];

  return (
    <Page
      title={<img src={duxlyLogo} alt="Duxly" style={{ height: '48px', objectFit: 'contain', marginTop: '8px', marginBottom: '8px' }} />}
    >
      <LegacyCard>
        <div style={{ padding: '16px' }}>
          <Text variant="headingMd">Products</Text>
          {isLoading ? (
            <ProgressBar progress={50} size="small" />
          ) : isError ? (
            <div style={{ color: '#bf0711', marginTop: '8px' }}>
              Error loading products: {error?.message}
              <Button onClick={refetch} plain>Retry</Button>
            </div>
          ) : (
            <div style={{ textAlign: 'right', fontSize: '12px', color: '#637381' }}>
              {products.length} products loaded
            </div>
          )}
        </div>
      </LegacyCard>

      <LegacyCard>
        <ResourceList
          items={products}
          selectedItems={selectedItems}
          onSelectionChange={handleSelectionChange}
          selectable
          bulkActions={bulkActions}
          filterControl={
            <Filters
              queryValue=""
              filters={[]}
              onQueryChange={() => {}}
              onQueryClear={() => {}}
              onClearAll={() => {}}
            >
              <ButtonGroup segmented>
                <Button pressed>All</Button>
                <Button>Queued</Button>
                <Button>Finished</Button>
              </ButtonGroup>
            </Filters>
          }
          renderItem={(item) => (
            <ResourceItem
              id={item.id}
              media={
                <img
                  src={item.image?.src || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_medium.png'}
                  alt={item.title}
                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                />
              }
              accessibilityLabel={`View details for ${item.title}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text variant="bodyMd" fontWeight="bold">
                  {item.title}
                </Text>
                <Badge status={item.status === 'active' ? 'success' : 'attention'}>
                  {item.status}
                </Badge>
                <Text variant="bodyMd" color="subdued">
                  {item.variants?.[0]?.price ? `$${item.variants[0].price}` : ''}
                </Text>
              </div>
            </ResourceItem>
          )}
        />
      </LegacyCard>

      <Modal
        open={modalActive}
        onClose={handleModalClose}
        title="Bulk Action"
        primaryAction={{
          content: 'Confirm',
          onAction: handleModalAction,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: handleModalClose,
        }]}
      >
        <Modal.Section>
          <Select
            label="Choose action"
            options={actionOptions}
            onChange={(value) => setBulkAction(value)}
            value={bulkAction}
          />
          <Text variant="bodyMd" as="p" color="subdued">
            {`Selected ${selectedItems.length} product${selectedItems.length === 1 ? '' : 's'}`}
          </Text>
        </Modal.Section>
      </Modal>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
        <Button>Add Products +</Button>
        <Button primary>Start new Bulk run</Button>
      </div>
    </Page>
  );
};

export default ProductList;
